import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarClock, MessageCircle, ChevronLeft, ChevronRight, List, CalendarDays } from "lucide-react";
import { openWhatsApp, appointmentConfirmMsg } from "../../lib/whatsapp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { StatusBadge, inputCls, PageHeader } from "./shared";

const STATUSES = ["all", "pending", "confirmed", "completed", "cancelled"];
const TIME_SLOTS = ["10:00 AM", "11:00 AM", "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM", "06:00 PM", "07:00 PM"];

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [filter, setFilter] = useState("all");
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [resched, setResched] = useState(null);
  const [reschedForm, setReschedForm] = useState({ date: "", time: "", staff_id: "", advance_amount: "", advance_method: "Cash" });
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", service_id: "", staff_id: "", date: "", time: "", status: "confirmed", notes: "", advance_amount: "", advance_method: "Cash" });
  const [view, setView] = useState("list");
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(() => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/appointments", { params }).then(({ data }) => setAppointments(data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data.filter((s) => s.category !== "Academy")));
    api.get("/staff").then(({ data }) => setStaff(data));
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openResched = (a) => {
    setResched(a);
    setReschedForm({ date: a.date, time: a.time, staff_id: a.staff_id || "", advance_amount: a.advance_amount ? String(a.advance_amount) : "", advance_method: a.advance_method || "Cash" });
  };

  const saveResched = async (e) => {
    e.preventDefault();
    if (!reschedForm.date || !reschedForm.time) { toast.error("Date and time required"); return; }
    try {
      await api.patch(`/appointments/${resched.id}`, { ...reschedForm, advance_amount: Number(reschedForm.advance_amount) || 0 });
      toast.success("Appointment updated");
      setResched(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      toast.success("Appointment deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.service_id || !form.date || !form.time) {
      toast.error("Fill all required fields");
      return;
    }
    try {
      await api.post("/appointments", { ...form, staff_id: form.staff_id || null, advance_amount: Number(form.advance_amount) || 0 });
      toast.success("Appointment created");
      setOpen(false);
      setForm({ customer_name: "", phone: "", email: "", service_id: "", staff_id: "", date: "", time: "", status: "confirmed", notes: "", advance_amount: "", advance_method: "Cash" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pad = (n) => String(n).padStart(2, "0");
  const byDay = appointments.reduce((acc, a) => { (acc[a.date] = acc[a.date] || []).push(a); return acc; }, {});
  const calY = calMonth.getFullYear();
  const calM = calMonth.getMonth();
  const daysInMonth = new Date(calY, calM + 1, 0).getDate();
  const firstDow = new Date(calY, calM, 1).getDay();
  const todayKey = new Date().toLocaleDateString("en-CA");
  const monthLabel = calMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const shiftMonth = (n) => { setCalMonth(new Date(calY, calM + n, 1)); setSelectedDay(null); };
  const STATUS_DOTS = { pending: "bg-[#C5A059]", confirmed: "bg-[#0A3F2F]", completed: "bg-emerald-500", cancelled: "bg-red-400" };
  const visible = view === "calendar" ? (selectedDay ? appointments.filter((a) => a.date === selectedDay) : []) : appointments;

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <PageHeader title="Appointments" subtitle="Manage bookings and their status."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-appointment-btn" className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
                <Plus className="w-4 h-4" /> New Appointment
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New Appointment</DialogTitle></DialogHeader>
              <form onSubmit={create} className="grid grid-cols-2 gap-3" data-testid="appointment-form">
                <input data-testid="appt-name-input" className={inputCls} placeholder="Customer name *" value={form.customer_name} onChange={set("customer_name")} />
                <input data-testid="appt-phone-input" className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
                <select data-testid="appt-service-select" className={`${inputCls} col-span-2`} value={form.service_id} onChange={set("service_id")}>
                  <option value="">Select service *</option>
                  {services.map((s) => <option key={s.id} value={s.id}>{`${s.name} — ₹${s.price}`}</option>)}
                </select>
                <select data-testid="appt-staff-select" className={`${inputCls} col-span-2`} value={form.staff_id} onChange={set("staff_id")}>
                  <option value="">Any available stylist</option>
                  {staff.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <input data-testid="appt-date-input" type="date" className={inputCls} value={form.date} onChange={set("date")} />
                <select data-testid="appt-time-select" className={inputCls} value={form.time} onChange={set("time")}>
                  <option value="">Time *</option>
                  {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input data-testid="appt-advance-input" type="number" min="0" step="any" className={inputCls} placeholder="Advance paid ₹ (optional)" value={form.advance_amount} onChange={set("advance_amount")} />
                <select data-testid="appt-advance-method-select" className={inputCls} value={form.advance_method} onChange={set("advance_method")}>
                  <option>Cash</option><option>UPI</option><option>Card</option>
                </select>
                <button type="submit" data-testid="appt-submit-btn" className="col-span-2 bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
                  Create Appointment
                </button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="flex gap-2 flex-wrap items-center">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`text-xs font-semibold px-4 py-2 rounded-full capitalize transition-colors ${
              filter === s ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"
            }`}>
            {s}
          </button>
        ))}
        <div className="ml-auto flex gap-1 bg-white border border-[#E5E7E5] rounded-full p-1">
          <button onClick={() => setView("list")} data-testid="view-list-btn"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors ${view === "list" ? "bg-[#0A3F2F] text-white" : "text-[#5C6A63]"}`}>
            <List className="w-3.5 h-3.5" /> List
          </button>
          <button onClick={() => { setView("calendar"); setSelectedDay(null); }} data-testid="view-calendar-btn"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors ${view === "calendar" ? "bg-[#0A3F2F] text-white" : "text-[#5C6A63]"}`}>
            <CalendarDays className="w-3.5 h-3.5" /> Calendar
          </button>
        </div>
      </div>
      {view === "calendar" && (
        <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm p-4" data-testid="appointments-calendar">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[#1C1C1C]" data-testid="calendar-month-label">{monthLabel}</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => shiftMonth(-1)} data-testid="calendar-prev-btn" className="p-1.5 rounded-full border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F] hover:text-[#0A3F2F]"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => { const d = new Date(); setCalMonth(new Date(d.getFullYear(), d.getMonth(), 1)); setSelectedDay(todayKey); }} data-testid="calendar-today-btn"
                className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F] hover:text-[#0A3F2F]">Today</button>
              <button onClick={() => shiftMonth(1)} data-testid="calendar-next-btn" className="p-1.5 rounded-full border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F] hover:text-[#0A3F2F]"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider text-[#5C6A63] mb-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDow }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const key = `${calY}-${pad(calM + 1)}-${pad(i + 1)}`;
              const dayAppts = byDay[key] || [];
              const isSel = selectedDay === key;
              const isToday = key === todayKey;
              const counts = dayAppts.reduce((m, a) => { m[a.status] = (m[a.status] || 0) + 1; return m; }, {});
              return (
                <button key={key} onClick={() => setSelectedDay(isSel ? null : key)} data-testid={`calendar-day-${key}`}
                  className={`min-h-[64px] rounded-md border p-1.5 text-left transition-colors ${isSel ? "border-[#0A3F2F] bg-[#0A3F2F]/5" : "border-[#E5E7E5] hover:border-[#0A3F2F]/50"} ${dayAppts.length && !isSel ? "bg-[#FAFAF8]" : ""}`}>
                  <span className={`text-xs font-bold ${isToday ? "bg-[#C5A059] text-[#1C1C1C] rounded-full px-1.5 py-0.5" : "text-[#1C1C1C]"}`}>{i + 1}</span>
                  {dayAppts.length > 0 && (
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      {Object.entries(counts).map(([st, n]) => (
                        <span key={st} className="flex items-center gap-0.5 text-[10px] font-semibold text-[#5C6A63]">
                          <span className={`w-2 h-2 rounded-full ${STATUS_DOTS[st] || "bg-gray-300"}`} />{n}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-[11px] text-[#5C6A63]">
            {Object.entries(STATUS_DOTS).map(([st, cls]) => <span key={st} className="flex items-center gap-1.5 capitalize"><span className={`w-2 h-2 rounded-full ${cls}`} />{st}</span>)}
          </div>
          {!selectedDay && <p className="text-xs text-[#5C6A63] mt-3" data-testid="calendar-hint">Tap a day to see its bookings below.</p>}
          {selectedDay && <p className="text-xs font-semibold text-[#0A3F2F] mt-3" data-testid="calendar-selected-label">Showing bookings for {selectedDay} ({(byDay[selectedDay] || []).length})</p>}
        </div>
      )}
      {(view === "list" || selectedDay) && (
      <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
              <th className="px-4 py-3 font-semibold">Customer</th>
              <th className="px-4 py-3 font-semibold">Service</th>
              <th className="px-4 py-3 font-semibold">Stylist</th>
              <th className="px-4 py-3 font-semibold">Date &amp; Time</th>
              <th className="px-4 py-3 font-semibold">Price</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="appointments-table-body">
            {visible.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#5C6A63]" data-testid="no-appointments">No appointments found.</td></tr>
            )}
            {visible.map((a) => (
              <tr key={a.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`appointment-row-${a.id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#1C1C1C]">{a.customer_name}</p>
                  {a.phone ? (
                    <span className="flex items-center gap-1.5 mt-0.5">
                      <a href={`tel:${a.phone}`} data-testid={`appt-phone-link-${a.id}`} className="text-xs font-semibold text-[#0A3F2F] hover:underline">{a.phone}</a>
                      <button onClick={() => openWhatsApp(a.phone, appointmentConfirmMsg(a))} data-testid={`appt-phone-wa-${a.id}`} title="WhatsApp this client"
                        className="text-emerald-600 hover:text-emerald-700"><MessageCircle className="w-3.5 h-3.5" /></button>
                    </span>
                  ) : (
                    <p className="text-xs text-[#5C6A63] italic" data-testid={`appt-no-phone-${a.id}`}>No phone provided</p>
                  )}
                </td>
                <td className="px-4 py-3">{a.service_name}</td>
                <td className="px-4 py-3 text-[#5C6A63]">{a.staff_name}</td>
                <td className="px-4 py-3 whitespace-nowrap">{a.date}<span className="text-[#5C6A63]"> · {a.time}</span></td>
                <td className="px-4 py-3 font-semibold text-[#0A3F2F]">
                  ₹{a.price.toLocaleString("en-IN")}
                  {a.advance_amount > 0 && (
                    <span className="block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#C5A059]/20 text-[#8a6d33] w-fit" data-testid={`advance-chip-${a.id}`}>
                      Adv ₹{a.advance_amount.toLocaleString("en-IN")} · {a.advance_method || "Cash"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <select data-testid={`status-select-${a.id}`} value={a.status} onChange={(e) => updateStatus(a.id, e.target.value)}
                      className="border border-[#E5E7E5] rounded-md text-xs px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]/40">
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <button onClick={() => openWhatsApp(a.phone, appointmentConfirmMsg(a))} data-testid={`whatsapp-appointment-${a.id}`} title="Send WhatsApp confirmation"
                      className="text-gray-400 hover:text-emerald-600 transition-colors">
                      <MessageCircle className="w-4 h-4" />
                    </button>
                    <button onClick={() => openResched(a)} data-testid={`reschedule-appointment-${a.id}`} title="Reschedule"
                      className="text-gray-400 hover:text-[#0A3F2F] transition-colors">
                      <CalendarClock className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(a.id)} data-testid={`delete-appointment-${a.id}`}
                      className="text-gray-400 hover:text-red-600 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      <Dialog open={!!resched} onOpenChange={(v) => !v && setResched(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reschedule — {resched?.customer_name}</DialogTitle></DialogHeader>
          <form onSubmit={saveResched} className="space-y-3" data-testid="reschedule-form">
            <p className="text-sm text-[#5C6A63]">{resched?.service_name} · currently {resched?.date} at {resched?.time}</p>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="resched-date-input" type="date" className={inputCls} value={reschedForm.date}
                onChange={(e) => setReschedForm({ ...reschedForm, date: e.target.value })} />
              <select data-testid="resched-time-select" className={inputCls} value={reschedForm.time}
                onChange={(e) => setReschedForm({ ...reschedForm, time: e.target.value })}>
                {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <select data-testid="resched-staff-select" className={inputCls} value={reschedForm.staff_id}
              onChange={(e) => setReschedForm({ ...reschedForm, staff_id: e.target.value })}>
              <option value="">Any available stylist</option>
              {staff.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="resched-advance-input" type="number" min="0" step="any" className={inputCls} placeholder="Advance paid ₹" value={reschedForm.advance_amount}
                onChange={(e) => setReschedForm({ ...reschedForm, advance_amount: e.target.value })} />
              <select data-testid="resched-advance-method-select" className={inputCls} value={reschedForm.advance_method}
                onChange={(e) => setReschedForm({ ...reschedForm, advance_method: e.target.value })}>
                <option>Cash</option><option>UPI</option><option>Card</option>
              </select>
            </div>
            <button type="submit" data-testid="resched-submit-btn"
              className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Save Changes
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
